import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRightToInformationWizardContext } from '../../../hooks/useCaseRequestWizard';
import { getAppUserId } from '../../../services/activeAppUser';
import { getInternalUserProfileById, type InternalUserProfile } from '../../../services/userProfile';
import { AUTH_STORAGE_EVENT } from '../../../utils/authStorage';
import { setFlashToast } from '../../../utils/flashToast';
import { SectionCard } from '../WizardComponents';
import {
  applySubmissionFieldsToDraft,
  submissionFieldsFromInternalProfile,
} from './rightToInformationSubmissionProfile';
import { RightToInformationWizardActions } from './RightToInformationWizardActions';
import { rightToInformationWizardPath, RightToInformationWizardShell } from './RightToInformationWizardShell';
import { useWizardFieldLabels } from '../useWizardFieldLabels';

function ReadOnlyField({ label, value }: { label: string; value: string }) {
  return (
    <div className="ccr-fg">
      <label>{label}</label>
      <input className="ccr-inp" type="text" readOnly disabled value={value || '—'} />
    </div>
  );
}

function displayField(value: string | null | undefined): string {
  const t = value?.trim();
  return t || '—';
}

export default function RightToInformationStep3SubmissionPage() {
  const f = useWizardFieldLabels();
  const nav = useNavigate();
  const { draft, setDraft } = useRightToInformationWizardContext();
  const [profile, setProfile] = useState<InternalUserProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const loadProfile = () => {
      setProfileLoading(true);
      const appUserId = getAppUserId();
      if (!appUserId) {
        setProfileLoading(false);
        setProfile(null);
        setFlashToast({ message: 'Could not load submitter details.', variant: 'error' });
        return;
      }
      void getInternalUserProfileById(appUserId).then((r) => {
        if (!mounted) return;
        setProfileLoading(false);
        if (!r.success) {
          setProfile(null);
          setFlashToast({ message: r.message ?? 'Could not load submitter details.', variant: 'error' });
          return;
        }
        setProfile(r.data);
        setDraft((d) => applySubmissionFieldsToDraft(d, submissionFieldsFromInternalProfile(r.data)));
      });
    };
    loadProfile();
    const onAuthStorage = (e: Event) => {
      const key = (e as CustomEvent<{ key?: string }>).detail?.key;
      if (key === 'appUser') loadProfile();
    };
    window.addEventListener(AUTH_STORAGE_EVENT, onAuthStorage);
    return () => {
      mounted = false;
      window.removeEventListener(AUTH_STORAGE_EVENT, onAuthStorage);
    };
  }, [setDraft]);

  const submitterFields = profileLoading
    ? {
        division: 'Loading…',
        submittedBy: 'Loading…',
        designation: 'Loading…',
        email: 'Loading…',
        contactNumber: 'Loading…',
      }
    : {
        division: displayField(profile?.divisionName),
        submittedBy: displayField(profile?.userDisplayName || profile?.username),
        designation: displayField(profile?.designation),
        email: displayField(profile?.username),
        contactNumber: displayField(profile?.contactNumber),
      };

  return (
    <RightToInformationWizardShell currentStep={3} draft={draft}>
      <SectionCard title={f.sectionRequestSubmitterDetails}>
        <div className="ccr-fr c2" style={{ marginBottom: 0 }}>
          <ReadOnlyField label="Submitted Division/ Unit" value={submitterFields.division} />
          <ReadOnlyField label="Designation" value={submitterFields.designation} />
          <ReadOnlyField label="Submitted by" value={submitterFields.submittedBy} />
          <ReadOnlyField label="Email Address" value={submitterFields.email} />
          <ReadOnlyField label="Contact Number" value={submitterFields.contactNumber} />
        </div>
      </SectionCard>

      <RightToInformationWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={3}
        onBack={() => nav(rightToInformationWizardPath(2, draft.draftRequestId))}
        onNext={() => nav(rightToInformationWizardPath(4, draft.draftRequestId))}
      />
    </RightToInformationWizardShell>
  );
}
